import { useLocation } from 'react-router-dom'

/** Locales the marketing site supports. English is served unprefixed at the root. */
export type Locale = 'en' | 'es' | 'pt'

export const SUPPORTED_LOCALES: Locale[] = ['en', 'es', 'pt']
export const DEFAULT_LOCALE: Locale = 'en'

const NON_DEFAULT_LOCALES = SUPPORTED_LOCALES.filter((l) => l !== DEFAULT_LOCALE)

const isNonDefaultLocale = (value: string | undefined): value is Locale =>
  NON_DEFAULT_LOCALES.includes(value as Locale)

/** Returns the locale encoded in the first path segment (e.g. '/es/...'), or 'en'. */
export function getCurrentLocale (pathname: string): Locale {
  const first = pathname.split('/').filter(Boolean)[0]
  return isNonDefaultLocale(first) ? first : DEFAULT_LOCALE
}

/** Strips a leading /es or /pt prefix, returning the base (English) path. */
export function stripLocale (pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (isNonDefaultLocale(segments[0])) {
    segments.shift()
  }
  return '/' + segments.join('/')
}

/** Builds the locale-prefixed path ('/es/link-in-bio'); English stays unprefixed. */
export function localizedPath (locale: Locale, basePath: string): string {
  const path = basePath.startsWith('/') ? basePath : `/${basePath}`
  if (locale === DEFAULT_LOCALE) return path
  return path === '/' ? `/${locale}` : `/${locale}${path}`
}

/** Hook: current locale derived from the route. */
export function useLocale (): Locale {
  const { pathname } = useLocation()
  return getCurrentLocale(pathname)
}

const creatorApp = String(process.env.REACT_APP_CREATOR_APP_URL)

/** Creator-app signup URL carrying the page's language. */
export function getSignupUrl (locale: Locale = DEFAULT_LOCALE): string {
  return `${creatorApp}/signup?lang=${locale}`
}

/** Creator-app login URL carrying the page's language. */
export function getLoginUrl (locale: Locale = DEFAULT_LOCALE): string {
  return `${creatorApp}/login?lang=${locale}`
}

/** Dashboard products a pricing CTA can deep-link to after signup. */
export type SignupProduct = 'autoDM' | 'linkInBio' | 'ama'

/**
 * Creator-app signup URL that tells the dashboard which product's plans to open once the user is
 * signed in. The dashboard keeps `product`/`audience` across signup, login and the Google popup.
 */
export function getProductSignupUrl (product: SignupProduct, audience: 'creator' | 'brand', locale: Locale = DEFAULT_LOCALE): string {
  const params = new URLSearchParams({ lang: locale, product, audience })
  return `${creatorApp}/signup?${params.toString()}`
}
